import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';

import { cn } from '@v2/lib/utils';
import {
  AVATAR_SIZES,
  USER_PHOTO_CARD_SIZES
} from '@cmp-src/components/UserAvatar/UserAvatar';
import { UserAvatar } from '@cmp-src/components/UserAvatar/UserAvatar';
import { getInitials } from '@gettactic/helpers';
import { getRandomColor } from '@gettactic/helpers/src/colors';

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
      className
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-muted',
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

const AvatarWithFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  {
    avatarSrc: string | null;
    userName: string;
    userAvatarProps?: React.ComponentPropsWithoutRef<typeof UserAvatar>;
    userFallbackProps?: React.ComponentPropsWithoutRef<typeof AvatarFallback>;
    avatarProps?: React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>;
    size?: AVATAR_SIZES;
    fallbackVariant?: keyof typeof FallbackClass;
  }
>(
  (
    {
      avatarSrc,
      userName,
      userAvatarProps: {
        className: userAvatarClassName,
        ...userAvatarProps
      } = {},
      userFallbackProps: {
        className: userFallbackClassName,
        style: userFallbackStyle,
        ...userFallbackProps
      } = {},
      avatarProps: { className: avatarPropsClassName, ...avatarProps } = {},
      size = AVATAR_SIZES.ROUNDED_8_8,
      fallbackVariant
    },
    ref
  ) => {
    const hasNoAvatarOrName = !avatarSrc && !userName;
    let avatar = null;
    // UserAvatar can display avatar or placeholder head,
    // if we don't even have a name
    if (avatarSrc || hasNoAvatarOrName) {
      avatar = (
        <UserAvatar
          name={userName}
          profile_photo_url={avatarSrc}
          size={size}
          {...userAvatarProps}
        />
      );
    } else {
      const initials = getInitials(userName || '');
      avatar = (
        <AvatarFallback
          className={cn(
            USER_PHOTO_CARD_SIZES[size].classNames,
            'rounded-full text-white',
            fallbackVariant ? FallbackClass[fallbackVariant] : '',
            userFallbackClassName
          )}
          style={{
            backgroundColor: getRandomColor({ string: userName || '' }),
            ...userFallbackStyle
          }}
          {...userFallbackProps}
        >
          {initials}
        </AvatarFallback>
      );
    }

    return (
      <Avatar
        ref={ref}
        className={cn(
          'items-center rounded-full',
          USER_PHOTO_CARD_SIZES[size].classNames,
          avatarPropsClassName
        )}
        {...avatarProps}
      >
        {avatar}
      </Avatar>
    );
  }
);

const FallbackClass = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-xs'
};

export { Avatar, AvatarImage, AvatarFallback, AvatarWithFallback };
